import { RotateCcw, Trash2 } from 'lucide-react'
import { useCallback } from 'react'
import { toast } from 'sonner'

import type { FlashcardResponse } from '@/api/types/flashcard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useFlashcardStore } from '@/store/useFlashcardStore'

const stateColor = (state: string | null | undefined) => {
  switch (state) {
    case 'New':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/30'
    case 'Learning':
    case 'Relearning':
      return 'bg-orange-500/10 text-orange-600 border-orange-500/30'
    case 'Review':
      return 'bg-green-500/10 text-green-600 border-green-500/30'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

const retrievabilityBar = (r: number | null | undefined) => {
  if (r == null) return null
  const pct = Math.round(r * 100)
  const color = pct > 80 ? 'bg-green-500' : pct > 50 ? 'bg-yellow-500' : 'bg-red-500'
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground w-8">{pct}%</span>
    </div>
  )
}

export const CardList = () => {
  const { cards, removeCard } = useFlashcardStore()

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        await removeCard(id)
        toast.success('カードを削除しました')
      } catch {
        toast.error('削除に失敗しました')
      }
    },
    [removeCard],
  )

  if (cards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-muted-foreground">カードがありません</p>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-3">
      {cards.map((card) => (
        <CardItem key={card.id} card={card} onDelete={handleDelete} />
      ))}
    </div>
  )
}

const CardItem = ({ card, onDelete }: { card: FlashcardResponse; onDelete: (id: number) => void }) => {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm truncate">{card.front_text}</p>
          <p className="text-xs text-muted-foreground truncate mt-1">{card.back_text}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Badge variant="outline" className={`text-[10px] ${stateColor(card.state)}`}>
            {card.state ?? 'New'}
          </Badge>
          {card.is_leech && (
            <Badge variant="destructive" className="text-[10px]">
              <RotateCcw className="w-2.5 h-2.5" />
            </Badge>
          )}
        </div>
      </div>

      {/* Retrievability bar */}
      <div className="mt-2">{retrievabilityBar(card.retrievability)}</div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex gap-1">
          <Badge variant="secondary" className="text-[10px]">
            {card.question_format}
          </Badge>
          <Badge variant="secondary" className="text-[10px]">
            {card.card_type === 'ai_generated' ? 'AI' : 'Ref'}
          </Badge>
        </div>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onDelete(card.id)}>
          <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
      </div>
    </Card>
  )
}
